




























































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';

import {
  VueMultiselectOptions,
  VueMultiselectProperties,
  SelectDropDownConfig
} from '@/models/select-dropdown';

import SelectDropDownFieldUtils from '@/utils/selectDropdownFieldUtils';

import CloseIcon from '@/components/icons/Close.vue';
import SearchIcon from '@/components/icons/Search.vue';
import InfoIcon from '@/components/icons/InfoV2.vue';
import Arrow from '@/components/icons/Arrow.vue';
import { highlightPhrase } from '@/utils';
import eventBus from '@/utils/eventBus';

export type SelectDropdownFieldVariant = 'normal' | 'new-field';

let timer: NodeJS.Timeout;

@Component<SelectDropdownField>({
  components: {
    Multiselect,
    CloseIcon,
    SearchIcon,
    InfoIcon,
    Arrow
  },
  $_veeValidate: {
    value() {
      const { value } = this as any;
      return value;
    },
    name() {
      const { name } = this as any;
      return name;
    }
  },
  computed: {
    optionalAfterVar() {
      return {
        '--optional-value': `" (${this.$t(`${this.tPrefix}.optional`)})"`
      };
    }
  }
})
export default class SelectDropdownField extends Vue {
  @Prop() instanceId!: string;
  @Prop() readonly label!: string;
  @Prop({ default: false }) isLoading?: boolean;
  @Prop({ default: () => [] }) options!: Array<VueMultiselectOptions>;
  @Prop({
    default: () => SelectDropDownFieldUtils.getSelectdropdownPropertiesDefault()
  })
  selectDropdownConfig!: SelectDropDownConfig;
  @Prop({ default: false }) readonly error!: boolean;
  @Prop({ default: false }) readonly optional?: boolean;
  @Prop({ default: true }) readonly required?: boolean;
  @Prop({ default: null }) initialValue!: VueMultiselectOptions;
  @Prop({ default: 'normal' }) variant?: SelectDropdownFieldVariant;
  @Prop({ default: '' }) helpText?: string;
  @Prop({ default: true }) showHelpIcon?: boolean;
  @Prop({ default: false }) emitInputEvent?: boolean;
  @Prop({ default: false }) setDefaultWhenInitialValueChanged?: boolean;
  @Prop({ default: false }) clearAfterSelect?: boolean;
  @Prop({ default: false }) listenerKeyboard?: boolean;
  @Prop({ default: false }) disableScrollOnFocus?: boolean;

  isFocused = false;
  modelValue: VueMultiselectOptions | null = null;

  textSearched = '';
  debounce: any = null;
  typing: boolean = false;
  vueMultiselectProperties: VueMultiselectProperties = SelectDropDownFieldUtils.getVueMultiselectProperties(
    { ...this.selectDropdownConfig }
  );

  @Watch('isFocused')
  changedTyping() {
    if (!this.disableScrollOnFocus) return;
    if (this.isFocused && this.$mobileCheck()) {
      eventBus.$emit('disableScroll');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 200);
    } else {
      eventBus.$emit('enableScroll');
    }
  }

  @Watch('error')
  onError(newErrorState: boolean) {
    if (newErrorState) {
      if (this.isSearchable) {
        this.inputOfSearch.setAttribute('aria-invalid', 'true');
      }

      return;
    }

    if (this.isSearchable) {
      this.inputOfSearch.removeAttribute('aria-invalid');
    }
  }

  getHighlightPhrase(value: string) {
    return highlightPhrase(
      value,
      this.textSearched,
      this.selectDropdownConfig.searchable
    );
  }

  keydownListerner(event: KeyboardEvent) {
    const ENTER = 13;

    if (
      event.keyCode === ENTER &&
      this.textSearched &&
      this.listenerKeyboard &&
      this.options.length &&
      !this.isLoading
    ) {
      this.$emit('key-enter');
      this.triggerClearAll();
    }
  }

  beforeDestroy() {
    this.clearTimeout();

    const input = this.$el.querySelector<HTMLInputElement>(
      '.multiselect__input'
    );
    if (input) input.removeEventListener('keydown', this.keydownListerner);
  }

  handleClose() {
    const input = this.$el.querySelector<HTMLInputElement>(
      '.multiselect__input'
    );
    if (input) input.removeEventListener('keydown', this.keydownListerner);

    this.$emit('close');
    this.clearTimeout();
  }

  clearTimeout() {
    if (timer) clearTimeout(timer);
  }

  handleOpen() {
    this.$emit('open');

    const input = this.$el.querySelector<HTMLInputElement>(
      '.multiselect__input'
    );
    if (input) input.addEventListener('keydown', this.keydownListerner);

    if (!this.modelValue || this.selectDropdownConfig.searchable) return;
    const optEl = document.querySelector<HTMLElement>(
      `#option-${this.modelValue.value}`
    );

    if (optEl) {
      timer = setTimeout(() => {
        optEl.scrollIntoView({ block: 'center' });
      }, 0);
    }
  }

  triggerSearchChange(textSearched: string) {
    this.textSearched = textSearched;

    if (!textSearched) {
      this.triggerClearAll();
      return;
    }

    if (!!this.modelValue && textSearched === this.modelValue.label) return;

    this.$emit('searchChange', textSearched);
  }

  triggerSelectOption(selectedOption: VueMultiselectOptions) {
    this.setValueInput(selectedOption.label);
    this.$emit('triggerSelect', selectedOption);

    if (this.clearAfterSelect) {
      this.triggerClearAll();
    }

    if (this.emitInputEvent) {
      this.$emit('input', selectedOption);
    }
  }

  triggerClearAll() {
    this.setValueInput();
    this.$emit('triggerClearValue');
    this.modelValue = null;
  }

  debounceSearch(inputValue: any) {
    this.typing = true;
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => {
      this.typing = false;
      this.triggerSearchChange(inputValue);
    }, this.selectDropdownConfig.debounceTime);
  }

  setValueInput(value = '') {
    if (this.isSearchable) this.inputOfSearch.value = value;
    this.textSearched = value;
    if (this.isSearchable) this.inputOfSearch.dispatchEvent(new Event('input'));
  }

  @Watch('initialValue')
  onInitialValueChange(newValue: { value: string; label: string }) {
    if (!this.setDefaultWhenInitialValueChanged) return;

    if (newValue && this.options.length) {
      this.modelValue =
        this.options.find(option => option.value === newValue.value) || null;

      if (this.modelValue) {
        this.setValueInput(this.modelValue.label);
      }
    }
  }

  mounted() {
    if (this.isSearchable) {
      this.inputOfSearch.setAttribute(
        'aria-errormessage',
        `err-${this.instanceId}`
      );

      this.inputOfSearch.addEventListener('focus', (evt: any) => {
        evt.target.setAttribute('autocomplete', 'off');

        this.isFocused = true;
      });

      this.inputOfSearch.addEventListener('blur', evt => {
        this.isFocused = false;
      });
    }

    if (this.initialValue.value && this.options.length) {
      this.modelValue =
        this.options.find(option => option.value === this.initialValue.value) ||
        null;

      if (this.modelValue) {
        this.setValueInput(this.modelValue.value);
      }
    }

    if (this.isSearchable) {
      this.inputOfSearch.setAttribute(
        'maxlength',
        this.selectDropdownConfig.maxlengthInput
      );
    }
  }

  get inputOfSearch() {
    return document.querySelector(
      `.${this.instanceId} .multiselect__input`
    ) as HTMLInputElement;
  }

  get isSearchable() {
    return this.selectDropdownConfig.searchable;
  }

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get tPrefix() {
    return 'common.components.selectDropdownField';
  }

  get translatedProperties() {
    const translatedObjects = Object.values(this.vueMultiselectProperties)
      .filter(key => SelectDropDownFieldUtils.keysForTranslate.includes(key))
      .reduce(
        (acc, key) => ({
          ...acc,
          [key]: this.$t(
            `${this.tPrefix}.${this.selectDropdownConfig.translateKey}.${key}`
          )
        }),
        {}
      );

    return { ...this.vueMultiselectProperties, ...translatedObjects };
  }
}
